/**
 * Knowledge systems list, backed by ``GET /api/systems``. Read-mostly, so the
 * query-client defaults (30s stale) are fine — no per-hook override.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useApi, type ApiError } from "@/lib/api";
import type { System } from "@/lib/types";

const systemsKey = ["systems"] as const;

export function useSystems() {
  const apiCall = useApi();
  return useQuery<System[], ApiError>({
    queryKey: systemsKey,
    queryFn: ({ signal }) => apiCall<System[]>("/api/systems", { signal }),
  });
}

/**
 * Admin: stamp ``LAST_VERIFIED`` — "a human confirmed this row is still true,
 * today". Seeded rows arrive NULL on purpose (see ``R__seed_systems.sql``), and
 * this endpoint is the *only* thing that advances the column.
 *
 * The response is the updated row with its attribution re-attached, so the
 * cached list takes a targeted swap rather than a refetch of all 60 rows. If
 * the list isn't cached (verified from a fresh mount), fall back to
 * invalidating — a no-op swap would otherwise drop the update on the floor.
 */
export function useVerifySystem() {
  const apiCall = useApi();
  const qc = useQueryClient();
  return useMutation<System, ApiError, string>({
    mutationFn: (systemId) =>
      apiCall<System>(`/api/systems/${systemId}/verify`, { method: "POST" }),
    onSuccess: async (updated) => {
      // A GET can be in flight right now: remounting the page after its 30s
      // stale window serves the cached list immediately and revalidates behind
      // it, and the card (and this button) are live during that window. That
      // GET was issued before the stamp existed, so letting it land would
      // silently undo the swap below. Cancelling aborts it — the query fn
      // forwards its signal to fetch — and reverts the query to its
      // pre-fetch state, which is what ``getQueryData`` then reads.
      await qc.cancelQueries({ queryKey: systemsKey });
      const cached = qc.getQueryData<System[]>(systemsKey);
      if (!cached) {
        void qc.invalidateQueries({ queryKey: systemsKey });
        return;
      }
      qc.setQueryData<System[]>(
        systemsKey,
        cached.map((s) => (s.system_id === updated.system_id ? updated : s)),
      );
    },
  });
}
